import { useState } from "react";
import { useNavigate } from 'react-router-dom'
import { 
  Button, 
  Container, 
  Heading, 
  VStack, 
  FormControl, 
  FormLabel, 
  Input, 
  Select, 
  useToast, 
  CheckboxGroup, 
  Checkbox,
  Card,
  CardBody,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Text,
  FormHelperText,
  Alert,
  AlertIcon,
  HStack,
  Divider,
  Badge,
  Box
} from "@chakra-ui/react";
import { ChevronRightIcon } from '@chakra-ui/icons'
import { Link as RouterLink } from 'react-router-dom'
import apiClient from "../../../../utils/apiClient";

const CreateApiKey = () => {
  // Get tenant ID from session storage
  const user = JSON.parse(sessionStorage.getItem("user"));
  const tenantId = user?.tenant_ids?.[0] || null;

  // Form state
  const [apiKeyName, setApiKeyName] = useState("");
  const [mode, setMode] = useState("sandbox");
  const [rateLimit, setRateLimit] = useState("");
  const [permissions, setPermissions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();
  const navigate = useNavigate()

  // Color mode values
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const cardBg = useColorModeValue('white', 'gray.700')

  const handleSubmit = async () => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    const payload = {
      tenant_id: tenantId,
      name: apiKeyName, 
      mode: mode,
      rate_limit: parseInt(rateLimit), 
      permissions: permissions
    }
    
    try {
      await apiClient('/api-keys', {
        method: 'POST',
        body: JSON.stringify(payload)
      })

      toast({
        title: 'Success',
        description: 'API key created successfully',
        status: 'success',
        duration: 3000
      })

      navigate('/settings/api-keys')
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = apiKeyName && mode && rateLimit && permissions.length > 0;

  return (
    <Container maxW="container.lg" py={6}>
      {/* Breadcrumb Navigation */}
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/settings">Settings</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/settings/api-keys">API Keys</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Create</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <VStack spacing={6} align="stretch">
        {/* Page Header */}
        <Box>
          <Heading size="lg" mb={2}>Create API Key</Heading>
          <Text color="gray.500">
            Create a new API key to access the CMS API. Choose the appropriate mode and permissions for your use case.
          </Text>
        </Box>

        {/* Form Card */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardBody>
            <VStack spacing={6} align="stretch">
              {/* API Key Information */}
              <Box>
                <Heading size="md" mb={4}>API Key Information</Heading>
                <VStack spacing={4} align="stretch">
                  <FormControl isRequired>
                    <FormLabel>API Key Name</FormLabel>
                    <Input 
                      value={apiKeyName} 
                      onChange={(e) => setApiKeyName(e.target.value)}
                      placeholder="Enter a descriptive name for your API key"
                    />
                    <FormHelperText>
                      Choose a name that helps you identify this key's purpose
                    </FormHelperText>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Environment Mode</FormLabel>
                    <Select value={mode} onChange={(e) => setMode(e.target.value)}>
                      <option value="sandbox">Sandbox</option>
                      <option value="production">Production</option>
                    </Select>
                    <FormHelperText>
                      {mode === 'sandbox' 
                        ? 'Safe for development and testing'
                        : 'Live environment - use with caution'
                      }
                    </FormHelperText>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Rate Limit (requests per minute)</FormLabel>
                    <Input
                      type="number"
                      value={rateLimit}
                      onChange={(e) => setRateLimit(e.target.value)}
                      placeholder="1000"
                      min="1"
                      max="10000"
                    />
                    <FormHelperText>
                      Maximum number of API requests allowed per minute
                    </FormHelperText>
                  </FormControl>
                </VStack>
              </Box>

              <Divider />

              {/* Permissions Section */}
              <Box>
                <Heading size="md" mb={4}>Permissions</Heading>
                <FormControl isRequired>
                  <FormLabel>Select API Permissions</FormLabel>
                  <CheckboxGroup colorScheme="blue" value={permissions} onChange={setPermissions}>
                    <VStack align="start" spacing={3}>
                      <Checkbox value="read">
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="medium">Read</Text>
                          <Text fontSize="sm" color="gray.500">
                            View and retrieve data from the API
                          </Text>
                        </VStack>
                      </Checkbox>
                      <Checkbox value="write">
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="medium">Write</Text>
                          <Text fontSize="sm" color="gray.500">
                            Create new content and data
                          </Text>
                        </VStack>
                      </Checkbox>
                      <Checkbox value="update">
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="medium">Update</Text>
                          <Text fontSize="sm" color="gray.500">
                            Modify existing content and data
                          </Text>
                        </VStack>
                      </Checkbox>
                      <Checkbox value="delete">
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="medium">Delete</Text>
                          <Text fontSize="sm" color="gray.500">
                            Remove content and data (use with caution)
                          </Text>
                        </VStack>
                      </Checkbox>
                    </VStack>
                  </CheckboxGroup>
                  <FormHelperText>
                    Select the minimum permissions required for your application
                  </FormHelperText>
                </FormControl>
              </Box>

              {/* Security Notice */}
              {mode === 'production' && (
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="medium">Production Mode Warning</Text>
                    <Text fontSize="sm">
                      This API key will have access to your live data. Store it securely and never expose it in client-side code.
                    </Text>
                  </VStack>
                </Alert>
              )}

              <Divider />

              {/* Action Buttons */}
              <HStack spacing={4} justify="flex-end">
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/settings/api-keys')}
                  isDisabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={handleSubmit}
                  isDisabled={!isFormValid}
                  isLoading={isSubmitting}
                  loadingText="Creating..."
                >
                  Create API Key
                </Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    </Container>
  );
};

export default CreateApiKey;
